import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Rarity } from '@anidex/shared/economy';
import type { AuctionDTO, ListingDTO, MarketHistoryItemDTO, Page, SystemBuybackResultDTO } from '@anidex/shared/types';
import { CARD_PAGE_SIZE, pageCursor } from '../../hooks/useListPage';
import { api, queryString } from '../api';
import { hasListIntent } from '../list-intent';
import { invalidateMarket, refreshMarketPurchase, refreshProgress } from './cache';
import { keys } from './keys';

export function useMarketListings(filters: {
  search?: string;
  rarity?: string;
  animeId?: number;
  characterId?: number;
  sort?: string;
  page?: number;
  limit?: number;
  
  browse?: boolean;
}) {
  const page = filters.page ?? 0;
  const limit = filters.limit ?? CARD_PAGE_SIZE;
  const enabled = filters.browse === true || hasListIntent(filters);
  return useQuery({
    queryKey: keys.market(filters),
    enabled,
    queryFn: () =>
      api<Page<ListingDTO>>(
        `/market${queryString({
          search: filters.search,
          rarity: filters.rarity,
          animeId: filters.animeId,
          characterId: filters.characterId,
          sort: filters.sort,
          limit,
          cursor: pageCursor(page, limit),
        })}`,
      ),
    placeholderData: enabled ? keepPreviousData : undefined,
    refetchInterval: 30_000,
  });
}

export function useBuybackRarity() {
  return useMutation({
    mutationFn: (rarity: Rarity) =>
      api<{ items: Array<{ characterId: number; net: number }> }>(`/collection/buyback${queryString({ rarity })}`),
  });
}

export function useSellCardsToSystem() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { characterIds: number[]; idempotencyKey: string }) =>
      api<SystemBuybackResultDTO>('/characters/sell', {
        body: { characterIds: input.characterIds },
        headers: { 'Idempotency-Key': input.idempotencyKey },
      }),
    onSuccess: (result) => refreshProgress(client, result.balance),
  });
}

export function useSellToSystem() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { characterId: number; idempotencyKey: string }) =>
      api<SystemBuybackResultDTO>(`/characters/${input.characterId}/sell`, {
        body: {},
        headers: { 'Idempotency-Key': input.idempotencyKey },
      }),
    onSuccess: (result) => refreshProgress(client, result.balance),
  });
}

export function useCreateListing() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { characterId: number; price: number; description?: string; idempotencyKey: string }) =>
      api<ListingDTO>('/market/listings', {
        body: { characterId: input.characterId, price: input.price, description: input.description },
        headers: { 'Idempotency-Key': input.idempotencyKey },
      }),
    onSuccess: () => invalidateMarket(client),
  });
}

export function useBuyListing() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { listingId: string; idempotencyKey: string }) =>
      api<{ listing: ListingDTO; balance: number }>(`/market/listings/${input.listingId}/buy`, {
        body: {},
        headers: { 'Idempotency-Key': input.idempotencyKey },
      }),
    onSuccess: (result) => refreshMarketPurchase(client, result.balance),
  });
}

export function useCancelListing() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (listingId: string) => api<{ ok: boolean }>(`/market/listings/${listingId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateMarket(client),
  });
}

export function useMarketMine(enabled = true) {
  return useQuery({
    queryKey: keys.marketMine,
    enabled,
    queryFn: () => api<{ listings: ListingDTO[] }>('/market/mine'),
  });
}

export function useMyAuctions(enabled = true) {
  return useQuery({
    queryKey: keys.auctionsMine,
    enabled,
    queryFn: () => api<{ auctions: AuctionDTO[] }>('/auctions/mine'),
  });
}

export function useCancelAuction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (auctionId: string) => api<{ ok: boolean }>(`/auctions/${auctionId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateMarket(client),
  });
}

export function useMarketHistory(page = 0, enabled = true) {
  const limit = CARD_PAGE_SIZE;
  return useQuery({
    queryKey: [...keys.marketHistory, page] as const,
    enabled,
    queryFn: () =>
      api<Page<MarketHistoryItemDTO>>(
        `/market/history${queryString({ cursor: pageCursor(page, limit), limit })}`,
      ),
    placeholderData: keepPreviousData,
  });
}

export function useAuctions(
  filters: {
    search?: string;
    rarity?: string;
    characterId?: number;
    sort?: string;
    page?: number;
    limit?: number;
    browse?: boolean;
  } = {},
) {
  const page = filters.page ?? 0;
  const limit = filters.limit ?? CARD_PAGE_SIZE;
  const enabled = filters.browse === true || hasListIntent(filters);
  return useQuery({
    queryKey: keys.auctions(filters),
    enabled,
    queryFn: () =>
      api<Page<AuctionDTO>>(
        `/auctions${queryString({
          search: filters.search,
          rarity: filters.rarity,
          characterId: filters.characterId,
          sort: filters.sort,
          limit,
          cursor: pageCursor(page, limit),
        })}`,
      ),
    placeholderData: enabled ? keepPreviousData : undefined,
    refetchInterval: 30_000,
  });
}

export function useAuction(id: string | undefined) {
  return useQuery({
    queryKey: keys.auction(id ?? ''),
    queryFn: () => api<AuctionDTO>(`/auctions/${id}`),
    enabled: Boolean(id),
    refetchInterval: (query) => (query.state.data?.status === 'active' ? 15_000 : false),
  });
}

export function useCreateAuction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      characterId: number;
      startingPrice: number;
      buyoutPrice?: number;
      durationHours: number;
      description?: string;
      idempotencyKey: string;
    }) =>
      api<AuctionDTO>('/auctions', {
        body: {
          characterId: input.characterId,
          startingPrice: input.startingPrice,
          buyoutPrice: input.buyoutPrice,
          durationHours: input.durationHours,
          description: input.description,
        },
        headers: { 'Idempotency-Key': input.idempotencyKey },
      }),
    onSuccess: () => invalidateMarket(client),
  });
}

export function usePlaceBid() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { auctionId: string; amount: number; idempotencyKey: string }) =>
      api<AuctionDTO>(`/auctions/${input.auctionId}/bid`, {
        body: { amount: input.amount },
        headers: { 'Idempotency-Key': input.idempotencyKey },
      }),
    onSuccess: async (auction) => {
      // The bid returns the complete detail; only the other market views need a refetch.
      await client.cancelQueries({ queryKey: keys.auction(auction.id), exact: true });
      client.setQueryData(keys.auction(auction.id), auction);
      invalidateMarket(client, auction.id);
    },
  });
}

export function useBuyout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { auctionId: string; idempotencyKey: string }) =>
      api<AuctionDTO>(`/auctions/${input.auctionId}/buyout`, {
        body: {},
        headers: { 'Idempotency-Key': input.idempotencyKey },
      }),
    onSuccess: () => invalidateMarket(client),
  });
}
